import L, { DivIcon, LatLngExpression, LayerGroup, Map, Polyline, PolylineOptions } from "leaflet";
import "leaflet-polylinedecorator";
import { useEffect, useState } from "react";
import { useMapEvents } from "react-leaflet";
import { ILine } from "./detour-lines";
import { LineColors } from "./line-colors";

enum DetourDirectionality {
  NO_DIRECTION = 0,
  BOTH = 1,
  A_B = 2,
  B_A = 3,
}

function getGeometry(data: ILine) {
  return data.refinedGeometry &&
    data.refinedGeometry.coordinates &&
    data.refinedGeometry.coordinates.length
    ? data.refinedGeometry
    : data.geometry;
}

const getRefinedDrawOptions = (color: string): PolylineOptions => ({
  color,
  weight: 4,
  opacity: 1,
});

/**
 This component renders the directional arrows which will describe whether the detour or blocked route is one-directional, one-directional(flipped) or bi-directional (Heaviliy borrowed from omleidingennet)
 */
export default function DirectionOverlay({
  data,
  showOnZoomLevel,
}: {
  data: ILine[];
  showOnZoomLevel?: number;
}) {
  const [layerGroupAdded, setLayerGroupAdded] = useState<boolean>();
  const [layerGroup] = useState<L.LayerGroup>(L.layerGroup([]));

  const isZoomedInEnough = (map: Map) => map.getZoom() >= (showOnZoomLevel ? showOnZoomLevel : 15);
  const map = useMapEvents({
    zoomend: () => {
      const isZoomedEnoughToShowIcons = isZoomedInEnough(map);

      if (isZoomedEnoughToShowIcons) {
        layerGroup.clearLayers();
        addLines(data);
      }
    },
  });

  const [defaultZoomLvl] = useState<number>(map.getZoom());

  function addLines(data: ILine[]) {
    (data || []).forEach((geodata) => {
      let positions = getGeometry(geodata).coordinates;
      positions = positions.map((pair) => [pair[1], pair[0]]);

      // @ts-ignore | new line will be automatically supported without change needed
      const color = LineColors[geodata.type] ?? LineColors.default;

      const polyline = L.polyline(positions as LatLngExpression[], {
        ...getRefinedDrawOptions(color),
      });

      const reversedLine = L.polyline([...positions].reverse() as LatLngExpression[], {
        ...getRefinedDrawOptions(color),
      });

      const zoomFactor = Math.abs(defaultZoomLvl / map.getZoom());
      const iconSize = 16;
      const arrowIconWidth = iconSize * (zoomFactor * 1.1);
      const arrowIconHeight = iconSize * (zoomFactor * 1.1);
      const circleIconSize = iconSize * (zoomFactor * 1.1);

      const arrowIcon = L.divIcon({
        html: `<svg xmlns="http://www.w3.org/2000/svg" width="${arrowIconWidth}" height="${arrowIconHeight}"  viewBox="0 0 73 84" preserveAspectRatio="xMidYMid meet">
        <path d="M72.8027 41.9999L0.80273 83.5691L0.802734 0.430661L72.8027 41.9999Z" fill="${color}" transform="rotate(-270 36.5 42)"/>
      </svg>      
      `,

        className: "",
        iconSize: [arrowIconWidth, arrowIconHeight],
        iconAnchor: [arrowIconWidth / 2, 0],
      });

      const circleIcon = L.divIcon({
        html: `<svg xmlns="http://www.w3.org/2000/svg" width="${circleIconSize}" height="${circleIconSize}" viewBox="0 0 72 73" preserveAspectRatio="xMidYMid meet">
        <circle cx="36" cy="36.5" r="36" fill="${color}"/>
      </svg>
      `,
        className: "",
        iconSize: [circleIconSize, circleIconSize],
        iconAnchor: [circleIconSize / 2, 0],
      });

      if (geodata.directionalImpact !== DetourDirectionality.NO_DIRECTION) {
        if (geodata.directionalImpact === DetourDirectionality.A_B) {
          addIconToLine(polyline, circleIcon, color, layerGroup);
          addIconToLine(reversedLine, arrowIcon, color, layerGroup);
        }

        if (geodata.directionalImpact === DetourDirectionality.B_A) {
          addIconToLine(polyline, arrowIcon, color, layerGroup);
          addIconToLine(reversedLine, circleIcon, color, layerGroup);
        }

        if (geodata.directionalImpact === DetourDirectionality.BOTH) {
          addIconToLine(polyline, arrowIcon, color, layerGroup);
          addIconToLine(reversedLine, arrowIcon, color, layerGroup);
        }
      }
    });
  }

  useEffect(() => {
    if (!layerGroupAdded) {
      layerGroup?.addTo(map);
      setLayerGroupAdded(true);
    }

    if (layerGroup) {
      layerGroup.clearLayers();
      addLines(data);
    }
  }, [layerGroup, data]);
  return null;
}

export function addIconToLine(
  line: Polyline,
  icon: DivIcon,
  color: string,
  toLayerGroup: LayerGroup<any>
) {
  L.polylineDecorator(line, {
    patterns: [
      {
        repeat: 0,
        symbol: L.Symbol.marker({
          rotate: true,
          markerOptions: {
            ...getRefinedDrawOptions(color),
            icon: icon,
          },
        }),
      },
    ],
  }).addTo(toLayerGroup);
}
